import { cx } from "@emotion/css";
import { Box, Grid, ListItem, ListItemButton, ListItemText, RadioGroup, Typography } from "@mui/material";
import { DRAWER_INFINITE_SCROLL_PAGE_SIZE, KEYBOARD_ARROW_DOWN, SEARCH_DELAY_DEBOUNCE } from "app/appConstants";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { GenericAbortSignal } from "axios";
import { MprcButton, MprcFormSubHeader, MprcRequestErrorReport, MprcSpinnerIndicator, MprcTypography } from "components";
import { MprcFilterNoRecords } from "components/filter/MprcFilterNoRecords";
import { IconButtonClearSearch } from "components/iconButtons/IconButtonClearSearch";
import { IconButtonPreview } from "components/iconButtons/IconButtonPreview";
import { TextFieldFilter } from "components/MprcAdvFilter";
import MprcAutoSizer from "components/MprcAutoSizer";
import { MprcBoxDrawer } from "components/MprcBoxDrawer";
import { MprcRadio } from "components/MprcRadio";
import { PublicationSelectorDto, PublicationSelectorDtoPaginatedList, PublicationType, SavePublicationResultDto } from "generated/CoreApi_v1";
import { isCanceledError } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";
import { publicationFactory } from "views/knowledge/feed/FeedHelpers";
import { PublicationViewerDialog } from "views/knowledge/publication/dialogs/PublicationViewerDialog";

////////////////////////////////////////////////////////////////////////////////

export type SelectorPublicationsFilter = {
    searchText: string;
    pageNumber: number;
    pageSize: number;
};

export enum SelectionOptions {
    Exist = 0,
    AddExclusive = 1,
}

type ItemData = {
    items: PublicationSelectorDto[];
    onItemClick: (item: PublicationSelectorDto) => void;
    onViewerClick: (item: PublicationSelectorDto) => void;
};

////////////////////////////////////////////////////////////////////////////////


/**
 * Renderiza uma linha da Lista.
 * @param props
 */
function RenderRow(props: ListChildComponentProps<ItemData>) {
    const { index, style } = props;
    const { items, onItemClick, onViewerClick } = props.data;

    if (index > items.length - 1) {
        return (
            <Box style={style} sx={{ mt: 1 }}>
                <MprcSpinnerIndicator />
            </Box>
        );
    }

    const item = items[index];

    return (
        <div key={index} style={style}>
            <ListItem
                disablePadding
                disableGutters
                dense
                secondaryAction={
                    <IconButtonPreview color="muted" onClick={() => onViewerClick(item)} size="small" />
                }
            >
                <ListItemButton disableGutters selected={item.selected} onClick={() => onItemClick(item)} sx={{ height: style.height }}>
                    <ListItemText title={item.title}>
                        <MprcTypography variant="h6" noWrapRows={2}>
                            {item.title}
                        </MprcTypography>
                    </ListItemText>
                </ListItemButton>
            </ListItem>
        </div>
    );
}

const MemoizedRenderRow = React.memo(RenderRow);

////////////////////////////////////////////////////////////////////////////////

export type PublicationSelectorDrawerResult = {
    publicationId: string;
    title: string;
    publicationType: PublicationType;
};

interface IPros {
    title?: string;
    subtitle?: string;
    createListExclusiveId?: string;
    emptyDataMessage?: string;
    onGetData: (filter: SelectorPublicationsFilter, signal: GenericAbortSignal | undefined) => Promise<PublicationSelectorDtoPaginatedList>;

    onSelect: (publication: PublicationSelectorDrawerResult) => void;
    onAddNewPost: (result: SavePublicationResultDto) => void;
    onClose: () => void;
}

/////////////////////////////////////////////////////////////////////////////////

export const ListPublicationSelectorDrawer: React.FC<IPros> = (props) => {
    const { t } = useTranslation("knowledge");

    const [error, setError] = useState<any>(null);
    const [searchText, setSearchText] = useState<string>("");
    const [selectionOption, setSelectionOption] = useState<SelectionOptions>(SelectionOptions.Exist);
    const [loading, setLoading] = useState<boolean>(true);
    const [items, setItems] = useState<PublicationSelectorDto[]>([]);
    const [dialog, setDialog] = useState<any>(null);

    const currentPage = useRef<number>(1);
    const ignoreSelectionHistory = useRef<boolean>(false);
    const endOfRecords = useRef<boolean>(true);
    const infiniteLoaderRef = useRef<InfiniteLoader>(null);
    const hasMountedRef = useRef(false);

    let searchTextRef = useRef<HTMLInputElement>(null);
    const abortControllerRef = useRef<AbortController | undefined>(undefined);

    const { title, subtitle, emptyDataMessage, createListExclusiveId, onClose, onSelect, onGetData, onAddNewPost } = props;

    ////////////////////////////////////////////////////////////////////////////////

    ///
    async function loadData() {
        try {
            abortControllerRef.current = new AbortController();

            var result = await onGetData({
                searchText: searchText,
                pageNumber: currentPage.current,
                pageSize: DRAWER_INFINITE_SCROLL_PAGE_SIZE
            }, abortControllerRef.current.signal);

            if (currentPage.current == 1) {
                // Reseta o cache.
                if (hasMountedRef.current) {
                    if (infiniteLoaderRef.current) {
                        infiniteLoaderRef.current.resetloadMoreItemsCache(true);
                    }
                }
                // Inicializa o array de itens.
                setItems(result.items);
            }
            else {
                // Adiciona mais itens no array.
                setItems([...items, ...result.items]);
            }

            // Há mais registros?
            endOfRecords.current = result.items.length < DRAWER_INFINITE_SCROLL_PAGE_SIZE;

            setTimeout(() => searchTextRef.current?.focus(), 100);
        }
        catch (e) {
            if (!isCanceledError(e)) {
                setError(e);
                endOfRecords.current = true;
            }
        }
        finally {
            setLoading(false);
            abortControllerRef.current = undefined;
        }

        setLoading(false);
    }

    ///
    const loadNextPage = (startIndex: number, stopIndex: number) => {
        currentPage.current++;
        return new Promise<void>((resolve) => {
            loadData().then(() => resolve());
        });
    };

    ///
    const resetSearch = (ignoreHistory: boolean = false) => {
        currentPage.current = 1;
        ignoreSelectionHistory.current = ignoreHistory;
        setLoading(true);
        loadData();
        hasMountedRef.current = true;
    };

    // Se há mais itens para ser carregado, adicionamos UMA LINHA para que o Indicador de Carregamento apareça.
    const itemCount = !endOfRecords.current ? items.length + 1 : items.length;

    // Permite apenas uma página de items pode vez.
    const loadMoreItems = loading ? () => { } : loadNextPage;

    // Verifica se a linha está carregada, exceto o Indicador de Carregamento.
    const isItemLoaded = (index: number) => endOfRecords.current || index < items.length;

    // Ao selecionar um item da lista.
    const onItemClick = (item: PublicationSelectorDto) => {
        setItems([...items.map(i => {
            if (i.publicationId == item.publicationId)
                i.selected = true;
            return i;
        })]);
        onSelect(item as PublicationSelectorDrawerResult);
    };

    // Ao clicar em visualizar uma publicação.
    const onViewerClick = (item: PublicationSelectorDto) => {
        setDialog(<PublicationViewerDialog
            publicationId={item.publicationId}
            onClose={() => setDialog(null)}
        />);
    };

    //
    const handleClose = (result: SavePublicationResultDto | null) => {
        if (result) onAddNewPost(result);
        setDialog(null);
    };

    //
    const handleAddNewExclusive = () => {
        setDialog(publicationFactory(
            null,
            PublicationType.Post,
            handleClose,
            createListExclusiveId
                ? { list: { listExclusiveId: createListExclusiveId } }
                : undefined
        ));
    };


    ////////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////////

    /// Executa a pesquisa depois de 600ms.
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            abortControllerRef.current?.abort();
            resetSearch();
        }, SEARCH_DELAY_DEBOUNCE);

        return () => {
            clearTimeout(delayDebounce);
            abortControllerRef.current?.abort();
        };
    }, [searchText]);


    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    ///
    const RenderOptions = (
        <Box mt={1}>
            <MprcFormSubHeader
                title={t("Publication.PublicationSelectorDrawer.WhatWantAddSubtitle")}
            />
            <Box m={1}>
                <RadioGroup value={selectionOption} onChange={e => setSelectionOption(e.target.value as unknown as SelectionOptions)}>
                    <MprcRadio value={SelectionOptions.Exist} label={t("Publication.PublicationSelectorDrawer.ExistingPublicationsLabel")} />
                    <MprcRadio value={SelectionOptions.AddExclusive} label={t("Publication.PublicationSelectorDrawer.AddNewExclusiveLabel")} />
                </RadioGroup>
            </Box>
        </Box>
    );

    ///
    const RenderFilter = (
        <TextFieldFilter
            autoFocus
            fullWidth
            autoComplete="off"
            placeholder={t("Publication.PublicationSelectorDrawer.SearchTextPlaceholder")}
            value={searchText}
            onChange={() => {
                setSearchText(searchTextRef.current?.value ?? "");
            }}
            onKeyDown={(e) => {
                if (e.keyCode == 13) resetSearch();
                else if (e.keyCode == KEYBOARD_ARROW_DOWN && searchTextRef.current?.value.length == 0) resetSearch(true);
            }}
            inputRef={searchTextRef}
            slotProps={{
                input: {
                    endAdornment:
                        searchText != "" &&
                        <IconButtonClearSearch
                            size="small"
                            onClick={() => setSearchText("")}
                        />
                }
            }}
        />
    );


    ///
    const RenderContentList = (
        <>
            {
                error
                    ? <MprcRequestErrorReport error={error} />
                    : loading
                        ? <MprcSpinnerIndicator />
                        : !loading && itemCount == 0
                            ? <MprcFilterNoRecords
                                filtered={searchText.length > 0}
                                message={
                                    emptyDataMessage
                                    ?? t(
                                        searchText.length > 0
                                            ? "Publication.PublicationSelectorDrawer.NoRecordsFoundText"
                                            : "Publication.PublicationSelectorDrawer.ListEmptyMessage"
                                    )
                                }
                            />
                            : <MprcAutoSizer>
                                {
                                    ({ width, height }) =>

                                        <InfiniteLoader
                                            isItemLoaded={isItemLoaded}
                                            itemCount={itemCount}
                                            loadMoreItems={loadMoreItems}
                                            ref={infiniteLoaderRef}
                                            threshold={10}
                                            minimumBatchSize={10}
                                        >
                                            {({ onItemsRendered, ref }) => (
                                                <FixedSizeList
                                                    height={height}
                                                    width={width}
                                                    itemData={{ items, onItemClick, onViewerClick }}
                                                    itemCount={itemCount}
                                                    itemSize={45}
                                                    onItemsRendered={onItemsRendered}
                                                    ref={ref}
                                                    className={cx(AppGlobalClassKeys.drawerFixedSizeList, AppGlobalClassKeys.thinScrollbar)}
                                                >
                                                    {MemoizedRenderRow}
                                                </FixedSizeList>
                                            )}
                                        </InfiniteLoader>
                                }
                            </MprcAutoSizer>
            }
        </>
    );

    ///
    const RenderContentAddNew = (
        <Grid container direction="column" alignItems="center">
            <Grid>
                <MprcButton
                    title={t("Publication.PublicationSelectorDrawer.AddExclusivePublicationButtonTitle")}
                    text={t("Publication.PublicationSelectorDrawer.AddExclusivePublicationButtonText")}
                    variant="text"
                    onClick={() => handleAddNewExclusive()}
                />
            </Grid>
            <Grid>
                <Box m={2}>
                    <Typography component="p" variant="caption">
                        {t("Publication.PublicationSelectorDrawer.ExclusiveTooltip")}
                    </Typography>
                </Box>
            </Grid>
        </Grid>
    );


    return (
        <>
            {dialog}

            <MprcBoxDrawer
                onClose={() => onClose()}
                title={title || t("Publication.PublicationSelectorDrawer.Title")}
                subtitle={subtitle}
                header={
                    <>
                        {
                            createListExclusiveId &&
                            RenderOptions
                        }

                        {
                            selectionOption == SelectionOptions.Exist
                                ? RenderFilter
                                : null
                        }
                    </>
                }
                content={
                    selectionOption == SelectionOptions.Exist
                        ? RenderContentList
                        : RenderContentAddNew
                }
            />
        </>
    );
};
